import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Button, Col, Row, Spinner } from 'reactstrap';
import DownloadIcon from 'mdi-react/DownloadIcon';
import PlayIcon from 'mdi-react/PlayIcon';
import Alert from '@/shared/components/Alert';
import { FileInputField } from '@/shared/components/form/FileInput';
import EntrataTestRunReport from './EntrataTestRunReport';
import { STEPS, currentStep } from './entrataTestRunSteps';
import { downloadEntrataExample, fetchEntrataTestProperties, runEntrataTest } from '../qaToolsApi';

const NOT_LOADED = null;

// The template sizes every button icon at 14px; these two buttons lead with a larger one.
const BUTTON_ICON_STYLE = { width: 20, height: 20, marginTop: 0 };
const FILE_INPUT_NAME = 'entrata-test-residents';

const propertyOption = property => ({
  value: property.id,
  label: `${property.program_name} (Entrata property ${property.entrata_property_id})`,
  property,
});

const saveFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Steps = ({ active }) => (
  <>
    <div className='wizard__steps'>
      {STEPS.map((step, index) => (
        <div key={step.title} className={`wizard__step mr-2${index === active ? ' wizard__step--active' : ''}`}>
          <p>{`${index + 1}. ${step.title}`}</p>
        </div>
      ))}
    </div>
    <p className='text-muted mt-2 mb-4'>{STEPS[active].hint}</p>
  </>
);

const EntrataTestRun = () => {
  const [availability, setAvailability] = useState(NOT_LOADED);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [isRunning, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [residents, setResidents] = useState(null);
  const [runs, setRuns] = useState(0);

  useEffect(() => {
    fetchEntrataTestProperties().then(result => {
      setAvailability(result.available);
      setProperties(result.properties);
    });
  }, []);

  const onPropertyChange = option => {
    setSelected(option);
    setResidents(null);
    setError(null);
    setRuns(0);
  };

  const onDownloadExample = async () => {
    setError(null);
    const download = await downloadEntrataExample(selected.value);

    if (download.errors) {
      setError(download.errors);
      return;
    }
    saveFile(download.file, `entrata-test-residents-${selected.property.entrata_property_id}.csv`);
  };

  const onRun = async () => {
    setRunning(true);
    setError(null);
    setResidents(null);

    const result = await runEntrataTest(selected.value, file.file);

    setRunning(false);
    if (result.errors) {
      setError(result.errors);
      return;
    }
    setResidents(result.residents);
    setRuns(count => count + 1);
  };

  if (availability === NOT_LOADED) {
    return <p>Loading...</p>;
  }

  if (!availability) {
    return <p>QA Tools are not available on this environment.</p>;
  }

  return (
    <>
      <div className='card__title'>
        <h5 className='subhead'>
          Runs the real Entrata sync for one property with residents from a file instead of Entrata,
          then shows what it changed on each of them.
        </h5>
      </div>

      <Steps active={currentStep({ hasProperty: Boolean(selected), runs })} />

      <Row>
        <Col lg={5} className='mb-3'>
          <span className='form__form-group-label'>Property</span>
          <Select
            className='react-select'
            classNamePrefix='react-select'
            options={properties.map(propertyOption)}
            value={selected}
            onChange={onPropertyChange}
            placeholder='Choose an Entrata property...'
          />
          <Button color='link' className='px-3 py-2 mt-2 mb-0 d-inline-flex align-items-center' disabled={!selected} onClick={onDownloadExample}>
            <DownloadIcon style={BUTTON_ICON_STYLE} className='mr-1' />
            Download example file
          </Button>
        </Col>
        <Col lg={5} className='mb-3'>
          <span className='form__form-group-label'>Resident file (CSV)</span>
          <FileInputField name={FILE_INPUT_NAME} value={file} onChange={setFile} />
          <small className='text-muted d-block mt-2'>Up to 50 residents, with the columns of the example file.</small>
        </Col>
        <Col lg={2} className='mb-3'>
          <span className='form__form-group-label d-block'>&nbsp;</span>
          <Button color='primary' size='sm' className='mb-0 pl-2 pr-4 text-nowrap d-inline-flex align-items-center' disabled={!selected || !file || isRunning} onClick={onRun}>
            {isRunning
              ? <><Spinner size='sm' className='mr-2' />Running...</>
              : <><PlayIcon style={BUTTON_ICON_STYLE} className='mr-1' />Run</>}
          </Button>
        </Col>
      </Row>

      <Alert color='info' className='alert--bordered' icon>
        <p>
          Like the daily sync, a run only acts on what changed since the last run, so the first run with a
          file saves the starting point and nothing changes yet.
        </p>
        <p>
          Outside production the sync stores resident emails in @temp- form, so a resident&apos;s email may
          show as changed after their first run.
        </p>
      </Alert>

      {error && <Alert color='danger' className='alert--bordered' icon><p>{error}</p></Alert>}

      {residents && (
        <>
          <div className='card__title mt-4'>
            <h5 className='bold-text'>Results</h5>
            <h5 className='subhead'>{`Run ${runs} · ${selected.label}`}</h5>
          </div>
          <EntrataTestRunReport residents={residents} />
        </>
      )}
    </>
  );
};

export default EntrataTestRun;
